import React, { useState, useEffect } from "react";
import { db } from "../firebase/firebase-config";
import { doc, getDoc, updateDoc, collection, addDoc } from "firebase/firestore";
import { useParams } from "react-router-dom";
import "../styles/PublicDogInfo.css";

const PublicDogInfo = () => {
    const [dog, setDog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [locationUpdated, setLocationUpdated] = useState(false); // Track location update
    const { dogId } = useParams();

    useEffect(() => {
        const fetchDogData = async () => {
            try {
                const docRef = doc(db, "dogs", dogId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const dogData = docSnap.data();
                    setDog(dogData);
                    const locationUpdateStatus = await updateLocation(docRef);
                    setLocationUpdated(locationUpdateStatus); // Update state based on location update status
                } else {
                    setError("No such dog found!");
                }
            } catch (err) {
                setError("Failed to fetch dog data.");
                console.error(err);
            }
            setLoading(false);
        };

        fetchDogData();
    }, [dogId]);

    useEffect(() => {
        const fetchAddressAndEmail = async () => {
            const emailSentKey = `emailSent_${dogId}`;
            const emailSent = sessionStorage.getItem(emailSentKey) === "true";
            if (dog && locationUpdated && !emailSent && dog.lastKnownLocation) {
                const fetchedAddress = await fetchAddress(
                    dog.lastKnownLocation.latitude,
                    dog.lastKnownLocation.longitude
                );
                await sendEmail(dog, fetchedAddress);
                sessionStorage.setItem(emailSentKey, "true"); // Persist the state in session storage
            }
        };
        fetchAddressAndEmail();
        // eslint-disable-next-line
    }, [dog, locationUpdated]);

    const updateLocation = async (docRef) => {
        if (navigator.geolocation) {
            return new Promise((resolve) => {
                navigator.geolocation.getCurrentPosition(
                    async (position) => {
                        const { latitude, longitude } = position.coords;
                        try {
                            // Update the document with the new location
                            await updateDoc(docRef, {
                                lastKnownLocation: { latitude, longitude },
                            });
                            console.log("Location updated!");
                            // Update dog state with the new location
                            setDog((prevDog) => ({
                                ...prevDog,
                                lastKnownLocation: { latitude, longitude },
                            }));
                            resolve(true);
                        } catch (error) {
                            console.error("Error updating location:", error);
                            resolve(false);
                        }
                    },
                    (error) => {
                        switch (error.code) {
                            case error.PERMISSION_DENIED:
                                console.error(
                                    "User denied the request for Geolocation."
                                );
                                break;
                            case error.POSITION_UNAVAILABLE:
                                console.error(
                                    "Location information is unavailable."
                                );
                                break;
                            case error.TIMEOUT:
                                console.error(
                                    "The request to get user location timed out."
                                );
                                break;
                            default:
                                console.error("An unknown error occurred.");
                                break;
                        }
                        resolve(false);
                    },
                    { timeout: 10000 } // Optional: Set a timeout for how long to wait for a location.
                );
            });
        } else {
            console.error("Geolocation is not supported by this browser.");
            return false;
        }
    };

    const fetchAddress = async (latitude, longitude) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            return data.display_name;
        } catch (error) {
            console.error("Error fetching address:", error);
            return "Address not found";
        }
    };

    const sendEmail = async (dogData, address) => {
        const emailSentKey = `emailSent_${dogId}`;
        const emailSent = sessionStorage.getItem(emailSentKey) === "true";
        if (!emailSent) {
            try {
                const mailRef = collection(db, "mail");
                await addDoc(mailRef, {
                    to: [dogData.email], // Owner's email address
                    message: {
                        subject: `${dogData.name}'s Information Accessed`,
                        text: `Hello, your dog's information with ID: ${dogId} was accessed. Here are the details:\n\nName: ${dogData.name}\nBreed: ${dogData.breed}\nDescription: ${dogData.description}\nLocation: ${address}`,
                        html: `
                        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; padding: 20px; border: 1px solid #ddd; border-radius: 10px; box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);">
                            <header style="text-align: center; padding-bottom: 20px; border-bottom: 1px solid #ddd;">
                                <h1 style="color: #333;">Tail Trails</h1>
                            </header>
                            <main>
                                <p style="color: #333;">Hello,</p>
                                <p style="color: #333;">Your dog's information with ID: <strong>${dogId}</strong> was accessed. Here are the details:</p>
                                <ul style="list-style-type: none; padding: 0;">
                                    <li style="margin-bottom: 10px;"><strong>Name:</strong> ${
                                        dogData.name
                                    }</li>
                                    <li style="margin-bottom: 10px;"><strong>Breed:</strong> ${
                                        dogData.breed
                                    }</li>
                                    <li style="margin-bottom: 10px;"><strong>Description:</strong> ${
                                        dogData.description
                                    }</li>
                                    <li style="margin-bottom: 10px;"><strong>Location:</strong> ${address}</li>
                                </ul>
                                ${
                                    dogData.photoUrl
                                        ? `<img src="${dogData.photoUrl}" alt="${dogData.name}" style="max-width: 100%; height: auto; border-radius: 8px; margin-top: 20px;" />`
                                        : ""
                                }
                            </main>
                            <footer style="text-align: center; padding-top: 20px; border-top: 1px solid #ddd; margin-top: 20px;">
                                <p style="color: #777;">Sincerely,</p>
                                <p style="color: #777;">Tail Trails</p>
                            </footer>
                        </div>`,
                    },
                });
                console.log("Email sent successfully!");
                sessionStorage.setItem(emailSentKey, "true");
            } catch (error) {
                console.error("Error sending email:", error);
            }
        }
    };

    if (loading)
        return (
            <div className="loading-message">
                <div className="1stSection">
                    <h2>Help Me Find My Way Home!</h2>
                    <h3>
                        Please share your location to allow my parents to know I
                        am safe!
                    </h3>
                </div>
                <div className="2ndSection">
                    <h4>Thank you for helping me get home!</h4>
                    <h3 className="Signature">
                        Sincerely,
                        <br />
                        Tail Trails
                    </h3>
                </div>
            </div>
        );
    if (error) return <div className="error-message">{error}</div>;
    if (!dog)
        return (
            <div className="error-message">No dog information available.</div>
        );

    return (
        <div className="dog-details public-dog-details">
            {dog.photoUrl && (
                <img
                    src={dog.photoUrl}
                    alt="Dog"
                    className="ModalImage publicImage"
                />
            )}
            <div className="info">
                <h2>{dog.name}</h2>
                <h4>{dog.breed}</h4>
                <h5>{dog.description}</h5>
                <div className="allInfo">
                    <div className="dogsInfo">
                        <p>
                            <strong>{dog.age}</strong>
                            <br />
                            Age
                        </p>
                        <p>
                            <strong>{dog.gender}</strong>
                            <br />
                            Gender
                        </p>
                    </div>
                    <div className="OwnersContactInfo publicOwnersInfo">
                        <p>
                            <strong>
                                <a
                                    href={`tel:${dog.phoneNumber.replace(
                                        /[^\d]/g,
                                        ""
                                    )}`}
                                >
                                    {dog.phoneNumber}
                                </a>
                            </strong>
                            <br />
                            Primary
                        </p>
                        <p>
                            <strong>
                                <a
                                    href={`tel:${dog.phoneNumber2.replace(
                                        /[^\d]/g,
                                        ""
                                    )}`}
                                >
                                    {dog.phoneNumber2}
                                </a>
                            </strong>
                            <br />
                            Secondary
                        </p>
                        <p>
                            <strong>
                                <a href={`mailto:${dog.email}`}>{dog.email}</a>
                            </strong>
                            <br />
                            Email
                        </p>
                        <p>
                            <strong>{dog.address}</strong>
                            <br />
                            Address
                        </p>
                    </div>
                    <div className="optionalDescriptions">
                        <p>
                            <strong>
                                {!dog.isFriendlyWithDogs
                                    ? `Not Friendly With Dogs`
                                    : `Friendly With Dogs`}
                            </strong>
                        </p>
                        <p>
                            <strong>
                                {!dog.isFriendlyWithKids
                                    ? `Not Friendly With Kids`
                                    : `Friendly With Kids`}
                            </strong>
                        </p>
                        <p>
                            <strong>
                                {dog.microchipped
                                    ? `Microchipped`
                                    : `Not Microchipped`}
                            </strong>
                        </p>
                        <p>
                            <strong>
                                {dog.rabiesShot
                                    ? `Has Rabies Shot`
                                    : `Does Not Have Rabies Shot`}
                            </strong>
                        </p>
                        <p>
                            <strong>
                                {dog.spayedNeutered
                                    ? `Is Spayed/Neutered`
                                    : `Is Not Spayed/Neutered`}
                            </strong>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicDogInfo;
